import React, { useCallback, useEffect, useRef, useState } from 'react';
import { dispatcher } from 'dispatcher';
import { throttle } from 'underscore';

import { EVENT_GAME_CHANGE_SCROLL } from 'js/app/components/main/Games/constants';
import { useGamesSelector } from 'js/app/components/main/Games/store/context';
import { clsx } from 'js/app/utils/clsx.util';
import { useMountEffect } from 'js/hooks/useMountEffect';

import style from './PageScroll.scss';

const pageSize = 12;

const checkLazy = ({ targetRef = null, currentIndex = 0, newIndexSetter }) => {
    if (!targetRef.current) {
        return;
    }

    if (targetRef.current.getBoundingClientRect().bottom <= window.innerHeight + window.scrollY) {
        newIndexSetter(currentIndex + pageSize);
    }
};

export const PageScroll = ({ children, isBingo }) => {
    const visibilityRef = useRef(null);
    const { viewModeDetailed } = useGamesSelector();
    const [visibleCursor, setVisibleCursor] = useState(pageSize);

    const handlerSetVisibleCursor = useCallback((size) => {
        setVisibleCursor(size);
        dispatcher.dispatch(EVENT_GAME_CHANGE_SCROLL);
    }, []);

    useMountEffect(() => {
        checkLazy({
            targetRef: visibilityRef,
            currentIndex: visibleCursor,
            newIndexSetter: handlerSetVisibleCursor
        });
    }, []);

    useEffect(() => {
        if (children.length < visibleCursor) {
            return;
        }

        const debouncedCheckLazy = throttle(
            () => checkLazy({
                targetRef: visibilityRef,
                currentIndex: visibleCursor,
                newIndexSetter: handlerSetVisibleCursor
            }),
            300
        );

        window.addEventListener('scroll', debouncedCheckLazy);
        window.addEventListener('resize', debouncedCheckLazy);

        return () => {
            window.removeEventListener('scroll', debouncedCheckLazy);
            window.removeEventListener('resize', debouncedCheckLazy);
        };
    }, [children.length, handlerSetVisibleCursor, visibleCursor]);

    return (
        <div
            ref={visibilityRef}
            className={clsx({
                [style.bingoBlockContainer]: isBingo,
                [style.gameBlockContainer]: !isBingo && viewModeDetailed,
                [style.gameListContainer]: !isBingo && !viewModeDetailed
            })}
        >
            {children.length > visibleCursor ? children.slice(0, visibleCursor) : children}
        </div>
    );
};
